import { getCard } from '@helpers/getCard'
import { cardNumberMask, dateMask } from '@helpers/masks'
import {
  InitialValuesTypes,
  useCheckoutContext
} from '@pages/Checkout/routes/contexts'
import {
  Icon,
  Image,
  Input,
  MaskedInput,
  Paragraph,
  Spacer,
  Spreader,
  Tooltip
} from '@ui-kit'
import {
  getCvvMaxLength,
  getCvvPlaceholderTranslationID,
  getTooltipImageURL
} from '@validations/CreditCard'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'simple-flexbox'

import styles from './Form.module.scss'

export const Form = () => {
  const { t } = useTranslation()

  const { formik } = useCheckoutContext()

  const { values, handleChange, handleBlur, errors, touched } = formik

  const { cardNumber, cardExpiry, cardCvv } = values

  const matchedCard = getCard(cardNumber)

  const hasFieldError = (name: keyof InitialValuesTypes) =>
    !!(touched[name] && errors[name])

  return (
    <Fragment>
      <Spacer space={30} />

      <MaskedInput
        id='cardNumber'
        value={cardNumber}
        onChange={handleChange}
        onBlur={handleBlur}
        mask={cardNumberMask}
        i18n={{
          placeholder: t('subscription.card.placeholder'),
          label: t('registration.flow.credit.card.step.card.number'),
          error: errors.cardNumber
        }}
        size={16}
        autoFocus
        hasError={hasFieldError('cardNumber')}
      />

      <Spacer space={30} />

      <div className={styles['form__cards-fields']}>
        <MaskedInput
          id='cardExpiry'
          value={cardExpiry}
          onChange={handleChange}
          onBlur={handleBlur}
          mask={dateMask}
          i18n={{
            placeholder: t('subscription.expiration.date.placeholder'),
            label: t('registration.flow.credit.card.step.expiry.date'),
            error: errors.cardExpiry
          }}
          size={16}
          hasError={hasFieldError('cardExpiry')}
        />

        <div>
          <Input
            id='cardCvv'
            value={cardCvv}
            onChange={handleChange}
            onBlur={handleBlur}
            i18n={{
              placeholder: t(
                matchedCard
                  ? getCvvPlaceholderTranslationID(matchedCard)
                  : 'subscription.cvv.placeholder.3'
              ),
              label: t('registration.flow.credit.card.step.cvv'),
              error: errors.cardCvv
            }}
            size={16}
            hasError={hasFieldError('cardCvv')}
            maxLength={getCvvMaxLength(matchedCard)}
          />

          <Tooltip
            className={styles['form__cards-fields--tooltip']}
            content={
              <Column>
                {t('subscription.credit.card.cvv.code')}

                <Spreader spread={5} />

                <Image src={getTooltipImageURL(matchedCard)} width='80px' />
              </Column>
            }
          >
            <Icon icon='icon-info-circle' size={14} color='neutral-6' />
          </Tooltip>
        </div>
      </div>

      <Spacer space={10} />

      <Paragraph size={12} color='neutral-6'>
        <Icon icon='icon-info-outline' color='neutral-6' />

        <Spreader spread={5} />

        {t('checkout.payment.method.credit.card.info')}
      </Paragraph>
    </Fragment>
  )
}

Form.displayName = 'CheckoutPaymentPaymentMethodForm'
